import React, { memo, useState, useRef, useEffect } from "react";
import { Pencil, ChevronDown } from "lucide-react";

// Function to format message text with line breaks and indentation
const formatMessageWithLineBreaks = (text) => {
  if (!text) return null;

  // Function to determine indentation level and apply appropriate list style
  const getFormattedLine = (line) => {
    // Count leading spaces in groups of 4 (one tab = 4 spaces)
    const leadingSpaces = line.match(/^(\s*)/)[0].length;
    const indentationLevel = Math.floor(leadingSpaces / 4);

    // Create the styled line with appropriate indentation
    let styledLine = line;

    // Check if the line contains list markers
    if (line.trim().match(/^\d+\.\s/)) {
      // This is a numbered list - keep as is
      // If there's indentation, we should already have transformed this in the input
      return styledLine;
    } else if (line.trim().match(/^[a-z]\.\s/)) {
      // This is an alphabetical list - keep as is
      return styledLine;
    } else if (line.trim().match(/^[ivxlcdm]+\.\s/i)) {
      // This is a Roman numeral list - keep as is
      return styledLine;
    }

    return styledLine;
  };

  return text.split("\n").map((line, i) => (
    <React.Fragment key={i}>
      <span
        style={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          overflowWrap: "break-word",
          display: "inline-block",
          width: "100%",
          textAlign: "left",
          paddingLeft: "0",
        }}
      >
        {getFormattedLine(line)}
      </span>
      {i < text.split("\n").length - 1 && <br />}
    </React.Fragment>
  ));
};

// Format recording time (seconds to MM:SS)
const formatRecordingTime = (seconds) => {
  if (!seconds) return "00:00";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
    .toString()
    .padStart(2, "0")}`;
};

// Optimize individual message rendering with memo
const Message = memo(
  ({
    msg,
    user,
    formatMessageTime,
    onEditStart,
    onEditCancel,
    onEditSave,
    isEditing,
  }) => {
    const isOwnMessage = msg.sender === user?._id;
    const [editText, setEditText] = useState(msg.message);
    const editInputRef = useRef(null);
    const [showEditButton, setShowEditButton] = useState(false);

    // Focus the input field when editing starts
    React.useEffect(() => {
      if (isEditing && editInputRef.current) {
        editInputRef.current.focus();
      }
    }, [isEditing]);

    // Update edit text if message is edited by someone else while in edit mode
    React.useEffect(() => {
      if (isEditing) {
        setEditText(msg.message);
      }
    }, [msg.message, isEditing]);

    // Auto-resize textarea when content changes
    React.useEffect(() => {
      if (isEditing && editInputRef.current) {
        // Reset height first to get the correct scrollHeight
        editInputRef.current.style.height = "auto";
        // Set the height based on scrollHeight, with a minimum of 200px
        const newHeight = Math.max(200, editInputRef.current.scrollHeight);
        editInputRef.current.style.height = `${newHeight}px`;
      }
    }, [editText, isEditing]);

    // Check if message is within edit window (24 hours)
    const isWithinEditWindow = (timestamp) => {
      if (!timestamp) return false;
      const msgTime = new Date(timestamp).getTime();
      const currentTime = new Date().getTime();
      const twentyFourHoursMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      return currentTime - msgTime < twentyFourHoursMs;
    };

    // Handle edit input keydown events
    const handleEditKeyDown = (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        onEditSave(msg, editText);
      } else if (e.key === "Escape") {
        onEditCancel();
      }
    };

    // Add this function to your component or utils
    function measureTextWidth(text, font) {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      context.font = font;

      // Find the longest line if there are multiple lines
      const lines = text.split("\n");
      let maxWidth = 0;

      lines.forEach((line) => {
        const metrics = context.measureText(line);
        maxWidth = Math.max(maxWidth, metrics.width);
      });

      return maxWidth;
    }

    return (
      <div
        className={`flex ${
          isOwnMessage ? "justify-end" : "justify-start"
        } relative group`}
      >
        <div
          className={`max-w-xs md:max-w-md px-4 py-3 rounded-2xl shadow-lg relative
                    ${
                      isOwnMessage
                        ? "bg-purple-700 text-white rounded-br-none"
                        : "bg-gray-800 text-gray-100 rounded-bl-none border border-gray-700"
                    }
                    animate-fadeIn motion-reduce:animate-none
                    ${msg.uploading ? "opacity-70" : ""}`}
          onMouseEnter={() => setShowEditButton(true)}
          onMouseLeave={() => setShowEditButton(false)}
        >
          {/* Edit button positioned inside the message */}
          {isOwnMessage &&
            !isEditing &&
            !msg.failed &&
            !msg.pending &&
            !msg.uploading &&
            msg.messageType !== "image" &&
            msg.messageType !== "audio" &&
            isWithinEditWindow(msg.createdAt) &&
            showEditButton && (
              <button
                onClick={() => onEditStart(msg)}
                className="absolute top-2 right-2 bg-purple-600 hover:bg-purple-500 text-white p-1.5 rounded-full shadow-md transition-colors z-10 opacity-0 group-hover:opacity-100"
                aria-label="Edit message"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}

          {!isOwnMessage && (
            <div className="font-medium text-sm text-purple-300">
              {msg.senderName || "Admin"}
            </div>
          )}

          {/* Show loader if message is uploading */}
          {msg.uploading && (
            <div className="flex items-center justify-center mb-2">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
              <span className="text-sm">Uploading...</span>
            </div>
          )}

          {/* Show image if message type is image */}
          {msg.messageType === "image" && (
            <div className="mb-2">
              {msg.fileUrl ? (
                <img
                  src={msg.fileUrl}
                  alt="Shared image"
                  className="rounded max-w-full max-h-60 object-contain"
                  onClick={() => window.open(msg.fileUrl, "_blank")}
                  style={{ cursor: "pointer" }}
                />
              ) : (
                msg.uploading && (
                  <div className="bg-gray-700 rounded h-40 w-full flex items-center justify-center">
                    <span className="text-gray-300">Image uploading...</span>
                  </div>
                )
              )}
            </div>
          )}

          {/* Show audio player if message type is audio */}
          {msg.messageType === "audio" && (
            <div className="mb-2">
              {msg.fileUrl ? (
                <div
                  className={`audio-message-container rounded-lg ${
                    isOwnMessage ? "bg-purple-800" : "bg-gray-700"
                  } p-3`}
                >
                  <div className="flex items-center mb-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-2 text-purple-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                      />
                    </svg>
                    <span className="text-xs font-medium text-purple-300">
                      Voice Message
                    </span>
                    {msg.duration && (
                      <span className="ml-auto text-xs opacity-80">
                        {formatRecordingTime(msg.duration)}
                      </span>
                    )}
                  </div>

                  <div className="audio-player-wrapper relative">
                    <audio
                      controls
                      src={msg.fileUrl}
                      className="w-[220px] audio-player"
                      preload="metadata"
                    >
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                </div>
              ) : (
                msg.uploading && (
                  <div
                    className={`rounded-lg ${
                      isOwnMessage ? "bg-purple-800" : "bg-gray-700"
                    } p-3 flex items-center justify-center`}
                  >
                    <div className="mr-2 animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span className="text-sm">Uploading audio...</span>
                  </div>
                )
              )}
            </div>
          )}

          {/* Message text with preserved line breaks and indentation (or editing interface) */}
          {msg.message &&
            msg.message !== "Image" &&
            msg.message !== "Audio message" &&
            (isEditing ? (
              <div className="edit-area w-[400px] mx-auto my-2">
                <textarea
                  ref={editInputRef}
                  value={editText}
                  onChange={(e) => {
                    setEditText(e.target.value);
                    // Auto-resize height on change
                    e.target.style.height = "auto";
                    e.target.style.height = `${Math.max(
                      200,
                      e.target.scrollHeight
                    )}px`;
                  }}
                  onKeyDown={handleEditKeyDown}
                  className="w-full text-sm bg-purple-800 text-white rounded p-4 focus:outline-none focus:ring-2 focus:ring-purple-300 font-mono"
                  placeholder="Edit your message..."
                  style={{
                    minHeight: "100px",
                    maxHeight: "500px",
                    // fontSize: "1rem",
                    // lineHeight: "1.5",
                    overflowY: "auto",
                    maxWidth: "100%",
                    resize: "vertical",
                    display: "block",
                    marginBottom: "10px",
                    borderWidth: "1px",
                    padding: "20px",
                  }}
                />
                <div className="flex space-x-4 mt-4 justify-end">
                  <button
                    onClick={onEditCancel}
                    className="text-base px-5 py-2 bg-gray-600 hover:bg-gray-500 rounded-md text-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => onEditSave(msg, editText)}
                    className="text-base px-5 py-2 bg-purple-600 hover:bg-purple-500 rounded-md text-white"
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm whitespace-pre-wrap font-mono pl-0">
                {formatMessageWithLineBreaks(msg.message)}
              </p>
            ))}

          {/* Message timestamp and status */}
          <div
            className={`text-xs mt-1 ${
              isOwnMessage ? "text-purple-200" : "text-gray-400"
            } text-right flex items-center justify-end`}
          >
            {formatMessageTime(msg.createdAt)}
            {msg.isEdited && <span className="ml-1">(edited)</span>}
            {msg.pending && <span className="ml-1">sending...</span>}
            {msg.failed && <span className="ml-1 text-red-400">failed</span>}
          </div>
        </div>
      </div>
    );
  }
);

const Messages = ({
  messages,
  loading,
  isLoadingOlder,
  messagesContainerRef,
  messageEndRef,
  user,
  formatMessageTime,
  onEditMessage,
}) => {
  const [editingMessageId, setEditingMessageId] = useState(null);
  const scrollPositionRef = useRef(null);
  const [hasScrolledUp, setHasScrolledUp] = useState(false);

  // Save scroll position before editing
  const handleEditStart = (msg) => {
    if (messagesContainerRef.current) {
      // Save current scroll position
      scrollPositionRef.current = messagesContainerRef.current.scrollTop;
    }
    setEditingMessageId(msg._id);
  };

  // Handler for canceling message edit
  const handleEditCancel = () => {
    setEditingMessageId(null);
    // Restore scroll position after a small delay to let the UI update
    setTimeout(() => {
      if (messagesContainerRef.current && scrollPositionRef.current !== null) {
        messagesContainerRef.current.scrollTop = scrollPositionRef.current;
      }
    }, 50);
  };

  // Handler for saving message edit
  const handleEditSave = (msg, newText) => {
    if (newText.trim() === msg.message.trim()) {
      // No change, just cancel
      handleEditCancel();
      return;
    }

    // Save current scroll position before updating
    if (messagesContainerRef.current) {
      scrollPositionRef.current = messagesContainerRef.current.scrollTop;
    }

    // Call parent component's edit handler
    onEditMessage(msg._id, newText, msg.chatRoomId || msg.roomId);
    setEditingMessageId(null);

    // Restore scroll position after a small delay to let the UI update
    setTimeout(() => {
      if (messagesContainerRef.current && scrollPositionRef.current !== null) {
        messagesContainerRef.current.scrollTop = scrollPositionRef.current;
      }
    }, 50);
  };

  const scrollToBottom = () => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (messagesContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } =
          messagesContainerRef.current;
        // Show button when scrolled up more than 100px from bottom
        const isScrolledUp = scrollHeight - scrollTop - clientHeight > 100;
        setHasScrolledUp(isScrolledUp);
      }
    };

    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }
  }, []);

  return (
    <div
      ref={messagesContainerRef}
      className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 md:space-y-6 bg-gray-900"
    >
      {/* Loading older messages indicator */}
      {isLoadingOlder && (
        <div className="flex justify-center py-3 sticky top-0 z-10">
          <div className="flex items-center gap-2 bg-gray-800 rounded-full px-4 py-2 shadow-lg border border-gray-700">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-500 border-t-transparent"></div>
            <span className="text-sm text-purple-300">
              Loading older messages...
            </span>
          </div>
        </div>
      )}

      {/* Initial loading state */}
      {loading && messages.length === 0 ? (
        <div className="flex justify-center items-center h-full">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
            <span className="text-sm text-purple-300">Loading messages...</span>
          </div>
        </div>
      ) : messages.length > 0 ? (
        // Message list - messages should already be sorted in chronological order (oldest first)
        <div className="space-y-4 md:space-y-6">
          {messages.map((msg) => (
            <Message
              key={msg._id}
              msg={msg}
              user={user}
              formatMessageTime={formatMessageTime}
              onEditStart={handleEditStart}
              onEditCancel={handleEditCancel}
              onEditSave={handleEditSave}
              isEditing={editingMessageId === msg._id}
            />
          ))}
        </div>
      ) : (
        // Empty state
        <div className="text-center py-8 text-gray-400">
          No messages yet. Start the conversation!
        </div>
      )}

      {/* Invisible element for scroll targeting */}
      <div ref={messageEndRef} />

      {/* Scroll to bottom button - WhatsApp style */}
      {hasScrolledUp && (
        <div
          style={{
            position: "fixed",
            bottom: "100px",
            right: "30px",
            zIndex: 9999,
          }}
        >
          <button
            onClick={scrollToBottom}
            className="bg-purple-600 hover:bg-purple-700 text-white p-3.5 rounded-full shadow-2xl hover:shadow-purple-500/30 transition-all duration-300 flex items-center justify-center"
            title="Go to bottom"
            style={{ width: "46px", height: "46px" }}
          >
            <ChevronDown size={22} strokeWidth={2.5} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Messages;
